import { CSSProperties, useId } from 'react';
import { formatFocusTime } from './focus-context';

interface Props {
  progress: number;
  remaining: number;
  durationSeconds: number;
  mode: 'focus' | 'break';
  compact?: boolean;
}

// Intentionally irregular: the right half is a woody stem while the left half
// loosens into fine roots, matching the focus concept rather than a UI gauge.
const ROOT_PATH = 'M 94 61 C 117 48 145 40 169 41 C 194 39 224 48 246 63 C 270 77 289 99 299 124 C 310 148 314 174 310 201 C 307 228 297 253 279 276 C 263 296 244 309 222 318 C 197 328 171 331 145 326 C 119 321 96 310 78 292 C 61 275 50 254 44 229 C 38 206 39 181 44 157 C 47 133 56 110 68 90 C 76 77 84 68 94 61';

export function FocusRootTimer({ progress, remaining, durationSeconds, mode, compact = false }: Props) {
  const uid = useId().replace(/:/g, '');
  const roughId = `focus-root-rough-${uid}`;
  const washId = `focus-root-wash-${uid}`;
  const inkId = `focus-root-ink-${uid}`;
  const elapsedPercent = Math.round(progress * 100);
  const remainingRatio = durationSeconds > 0 ? Math.min(1, Math.max(0, remaining / durationSeconds)) : 0;
  const style = {
    '--focus-remaining': `${Math.max(1, remainingRatio * 100)} 100`,
    '--focus-ratio': progress,
  } as CSSProperties;

  return <div className={`focus-root-timer${compact ? ' focus-root-timer--compact' : ''}${mode === 'break' ? ' is-break' : ''}`} style={style}>
    <svg viewBox="0 0 360 360" role="img" aria-label={`${mode === 'break' ? '休息' : '专注'}已进行 ${elapsedPercent}%`}>
      <defs>
        <filter id={roughId} x="-12%" y="-12%" width="124%" height="124%">
          <feTurbulence type="fractalNoise" baseFrequency=".012 .18" numOctaves="2" seed="17" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="2.8" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={washId} x="-35%" y="-35%" width="170%" height="170%">
          <feGaussianBlur stdDeviation="10" />
        </filter>
        <linearGradient id={inkId} x1=".18" y1=".05" x2=".84" y2=".95">
          <stop offset="0" stopColor="#776b79" />
          <stop offset=".42" stopColor="#4c424c" />
          <stop offset=".82" stopColor="#665a5f" />
          <stop offset="1" stopColor="#877a78" />
        </linearGradient>
      </defs>

      <g className="focus-root-watercolor" aria-hidden="true" filter={`url(#${washId})`}>
        <path d="M109 100c35-35 93-46 137-21 45 26 69 80 57 129-12 51-59 96-113 101-55 5-106-26-125-75-19-48 2-101 44-134Z" />
        <path d="M180 63c49 0 91 27 111 69 20 43 12 94-19 129-31 36-86 51-131 34-45-17-77-61-75-109 2-66 52-123 114-123Z" />
      </g>

      <g className="focus-root-skeleton" aria-hidden="true">
        <path className="focus-root-track" d={ROOT_PATH} pathLength="100" />
        <g className="focus-root-fibres focus-root-fibres--left">
          <path d="M93 62C76 52 69 38 70 20m13 38C68 61 54 56 42 45m31 20C58 57 48 45 43 31" />
          <path d="M76 80C57 75 42 65 31 49m29 25C47 72 34 76 21 86m35-3C42 89 31 99 24 112" />
          <path d="M62 103C45 97 32 86 22 69m31 42C37 111 24 117 13 128m36-3C34 132 24 142 17 156" />
          <path d="M48 150C31 143 19 131 11 115m35 55C30 165 17 168 6 178m40 12C29 189 16 195 7 208" />
          <path d="M49 207C32 211 20 220 12 235m43-9C39 232 29 244 23 260m37-15C45 250 34 262 28 278" />
          <path d="M68 267C54 278 47 291 46 307m31-28C63 294 58 310 61 327m24-38C75 306 74 320 80 335" />
          <path d="M103 306C94 321 92 335 98 349m20-36C111 327 112 340 119 352m14-31C128 335 130 345 137 354" />
        </g>
        <g className="focus-root-fibres focus-root-fibres--crown">
          <path d="M154 40c-5-12-4-23 4-34" /><path d="M175 36c1-13 7-24 18-32" />
          <path d="M202 38c8-13 18-21 31-24" /><path d="M226 45c10-10 22-14 37-13" />
        </g>
      </g>

      <g className="focus-root-wood" aria-hidden="true" filter={`url(#${roughId})`}>
        <path className="focus-root-growth focus-root-growth--shadow" d={ROOT_PATH} pathLength="100" />
        <path className="focus-root-growth" d={ROOT_PATH} pathLength="100" stroke={`url(#${inkId})`} />
        <path className="focus-root-bark" d="M101 58C145 39 204 38 248 65M316 187c-2 48-27 91-69 118M224 322c-43 15-91 7-126-22" />
      </g>

      <g className="focus-root-branches" aria-hidden="true">
        <g className="branch branch--top">
          <path d="M236 52c8-8 13-19 14-32m-8 25c-10-5-17-12-21-22m25 11c10-2 18-7 25-15" />
          <path className="leaf" d="M249 21c-2-13 5-22 13-27 4 12 0 22-13 27Z" />
          <path className="leaf leaf--small" d="M224 25c-10-4-15-11-15-20 10 1 16 8 15 20Z" />
          <path className="leaf leaf--small" d="M258 29c9-8 18-8 25-4-5 10-14 12-25 4Z" />
        </g>
        <g className="branch branch--upper-right">
          <path d="M294 103c13-2 24-10 32-23m-21 16c1-12 7-21 17-28m-7 20c10 1 18-2 25-8" />
          <path className="leaf" d="M325 80c-1-13 6-22 15-26 3 12-2 21-15 26Z" />
          <path className="leaf leaf--small" d="M315 89c10-7 19-6 25-1-7 9-15 10-25 1Z" />
        </g>
        <g className="branch branch--right">
          <path d="M319 157c12-5 21-14 27-27m-19 22c10 2 19 0 27-7m-32 34c12 2 23 8 31 18" />
          <path className="leaf" d="M345 132c1-14 9-22 19-25 1 13-5 21-19 25Z" />
        </g>
        <g className="branch branch--lower-right">
          <path d="M293 267c14 0 27 6 38 17m-25-13c4-11 11-19 21-24m-16 31c11 5 19 13 24 24" />
          <path className="leaf" d="M327 249c4-13 13-19 24-19-2 13-10 20-24 19Z" />
        </g>
        <g className="branch branch--bottom">
          <path d="M221 323c8 9 12 20 12 33m-4-24c11-3 21-1 30 5" />
          <path className="leaf" d="M233 347c-10 8-20 8-28 3 7-9 16-11 28-3Z" />
        </g>
        <g className="branch branch--lower-left">
          <path d="M119 316c-8 8-13 18-15 30m9-22c-10-2-19 0-27 7" />
          <path className="leaf leaf--small" d="M105 340c-10 5-18 2-23-5 8-6 16-4 23 5Z" />
        </g>
      </g>

      <circle className="focus-root-seed" cx="93" cy="62" r="3.2" aria-hidden="true" />
    </svg>
    <div><small>{mode === 'break' ? '松一松，留一点空白' : progress > 0 ? '专注一刻' : '准备开始'}</small><strong>{formatFocusTime(remaining)}</strong><span>/ {Math.round(durationSeconds / 60)}:00</span></div>
  </div>;
}
